import * as React from 'react';
import { Alert, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { AI_LABELS, getAiRuns } from '../../Api';
import { AI_RUN_STATUSES, AiRunStatus, AiRunSummaryDto } from '../../models/dtos/AiDto';
import {
    AiActionError, AiErrorAlert, aiLoadErrorText, AiRunStatusChip, EnumSelect, formatAiDate, formatAiDuration, formatAiUsd,
} from './AiShared';
import { AiExportOutcome, AiRunExportButton } from './AiBundleExport';

/** Paged runs (server paging). With `fixedAgentCode` the agent filter is hidden. */
export default function AiRunsGrid({ fixedAgentCode, reloadKey = 0, pageSize = 25 }: {
    fixedAgentCode?: string;
    reloadKey?: number;
    pageSize?: number;
}) {
    const navigate = useNavigate();
    const [agentCode, setAgentCode] = React.useState('');
    const [status, setStatus] = React.useState<AiRunStatus | ''>('');
    const [endUserId, setEndUserId] = React.useState('');
    const [topLevelOnly, setTopLevelOnly] = React.useState(true);
    const [debounced, setDebounced] = React.useState({ agentCode: '', endUserId: '' });
    const [rows, setRows] = React.useState<AiRunSummaryDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [exportError, setExportError] = React.useState<AiActionError | null>(null);
    const [exportNotice, setExportNotice] = React.useState<string | null>(null);
    const onExport = (outcome: AiExportOutcome) => {
        setExportError(outcome.error);
        setExportNotice(outcome.notice);
    };
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize });

    React.useEffect(() => {
        const timer = window.setTimeout(() => setDebounced({ agentCode: agentCode.trim(), endUserId: endUserId.trim() }), 400);
        return () => window.clearTimeout(timer);
    }, [agentCode, endUserId]);

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [debounced, status, topLevelOnly]);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getAiRuns({
            agentCode: fixedAgentCode ?? debounced.agentCode,
            status: status || undefined,
            endUserId: debounced.endUserId || undefined,
            topLevelOnly,
        }, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(AI_LABELS.runs, 'Failed to load runs.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [fixedAgentCode, debounced, status, topLevelOnly, paginationModel]);

    React.useEffect(() => { load(); }, [load, reloadKey]);

    const columns: GridColDef<AiRunSummaryDto>[] = [
        {
            field: 'runId', headerName: 'Run', width: 120, sortable: false,
            renderCell: ({ row }) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.runId.slice(0, 8)}…</Typography>,
        },
        { field: 'createdAt', headerName: 'Created', width: 170, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        ...(fixedAgentCode ? [] : [{ field: 'agentCode', headerName: 'Agent', width: 180, sortable: false } as GridColDef<AiRunSummaryDto>]),
        { field: 'status', headerName: 'Status', width: 120, sortable: false, renderCell: ({ row }) => <AiRunStatusChip status={row.status} /> },
        { field: 'caller', headerName: 'Caller', width: 140, sortable: false },
        { field: 'endUserId', headerName: 'End user', width: 100, sortable: false, valueFormatter: ({ value }) => (value == null ? '—' : `#${value}`) },
        {
            field: 'subject', headerName: 'Subject', width: 160, sortable: false,
            valueGetter: ({ row }) => (row.subjectType ? `${row.subjectType} / ${row.subjectId ?? ''}` : '—'),
        },
        { field: 'turns', headerName: 'Turns', width: 70, sortable: false },
        { field: 'depth', headerName: 'Depth', width: 70, sortable: false },
        { field: 'costUsd', headerName: 'Cost', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        {
            field: 'duration', headerName: 'Duration', width: 100, sortable: false,
            valueGetter: ({ row }) => formatAiDuration(row.startedAt, row.finishedAt),
        },
        {
            field: 'error', headerName: 'Error', flex: 1, minWidth: 200, sortable: false,
            renderCell: ({ row }) => row.error
                ? <Typography variant="caption" color="error" sx={{ whiteSpace: 'normal' }}>{row.error}</Typography>
                : null,
        },
        {
            field: 'export', headerName: '', width: 56, sortable: false, filterable: false, align: 'center',
            renderCell: ({ row }) => <AiRunExportButton runId={row.runId} iconOnly onOutcome={onExport} />,
        },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                {!fixedAgentCode && (
                    <TextField size="small" label="Agent code" value={agentCode} onChange={e => setAgentCode(e.target.value)} />
                )}
                <EnumSelect size="small" label="Status" value={status} options={AI_RUN_STATUSES} allowEmpty fullWidth={false}
                    sx={{ minWidth: 160 }} onChange={setStatus} />
                <TextField size="small" label="End user id" value={endUserId} onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))} />
                <FormControlLabel control={<Switch checked={topLevelOnly} onChange={e => setTopLevelOnly(e.target.checked)} />} label="Top-level only" />
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <AiErrorAlert error={exportError} onClose={() => setExportError(null)} />
            {exportNotice && <Alert severity="success" onClose={() => setExportNotice(null)}>{exportNotice}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.runId}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                onRowClick={({ row }) => navigate(`/ai/runs/${encodeURIComponent((row as AiRunSummaryDto).runId)}`)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 1 } }}
                localeText={{ noRowsLabel: 'No runs' }}
            />
        </Stack>
    );
}
